/**
 * @file Cross-cutting constants. Money is INTEGER CENTS everywhere (CLAUDE.md).
 *
 * Fee math lives in ./fees.ts (T2.2); the lease transition map and its schemas
 * in ./leases.ts (T1.2). This file holds only the values that are stable from
 * day zero.
 */

/** The smallest currency unit we ever round a fee to: one nickel ($0.05). */
export const NICKEL_CENTS = 5;

/** Dollars-to-cents conversion; avoids floating-point money (INV-MONEY-1). */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/**
 * Cents back to a display string.
 *
 * The inverse of `dollarsToCents`, and it belongs next to it: a form that converts
 * dollars to cents in one place and back in another is how a "$1,850.50" becomes
 * "1850.5" and then 18505. Always two decimals, because money shown without them
 * reads as a rounded figure.
 */
export function centsToDollarString(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * The shape typed money may take once `$`, `,` and whitespace are stripped: digits,
 * optionally a point, then AT MOST two digits. A third decimal is refused rather
 * than rounded — "1.005" is a typo, not a hundred cents — and so is anything a
 * float parser would accept and a person would not type on a rent form (`1e3`,
 * `+5`, `-5`, `Infinity`).
 */
const DOLLAR_INPUT = /^(\d*)(?:\.(\d{0,2}))?$/;

/**
 * Parse user-typed money into integer cents, or null when the field is blank.
 *
 * STRING arithmetic, never a float: `Math.round(parseFloat("4.35") * 100)` is 434
 * on some engines, because 4.35 has no exact binary form. Here the whole dollars
 * and the (right-padded) cents are read as two integers and combined, so the value
 * a person typed is the value stored, to the cent, every time.
 *
 * Returns `undefined` for something unparseable, so a caller can refuse to submit
 * rather than send NaN — which `JSON.stringify` turns into `null`, silently
 * clearing the field instead of reporting the typo.
 */
export function parseDollarInput(input: string): number | null | undefined {
  const cleaned = input.replace(/[$,\s]/g, "");
  if (cleaned.length === 0) return null;
  const match = DOLLAR_INPUT.exec(cleaned);
  if (match === null) return undefined;
  const whole = match[1] ?? "";
  const fraction = match[2] ?? "";
  // "." names no digit at all; ".5" and "5." each name one.
  if (whole.length === 0 && fraction.length === 0) return undefined;
  const cents =
    (whole.length === 0 ? 0 : Number.parseInt(whole, 10)) * 100 +
    (fraction.length === 0 ? 0 : Number.parseInt(fraction.padEnd(2, "0"), 10));
  return Number.isSafeInteger(cents) ? cents : undefined;
}

/** fee_payer — which party funds the Stripe processing fee. */
export const FEE_PAYER = ["tenant", "landlord"] as const;
export type FeePayer = (typeof FEE_PAYER)[number];

/** workspace role enum — THE authorization source (§5). */
/**
 * The shortest password the product accepts, enforced by the auth provider on the API and
 * restated by the sign-up form so a short one is caught before the round trip. One constant,
 * because two literals are how a form comes to promise something the server then refuses.
 * Eight is the provider's default and is on the short side for an account that can see tenants'
 * contact details and rent history.
 */
export const MIN_PASSWORD_LENGTH = 10;

/**
 * How long an emailed link works, in minutes. One number per link kind, shared so the email, the
 * on-screen copy and the server's token lifetime cannot drift apart. Short on purpose: a reset link
 * is a credential sitting in an inbox. A person who misses it asks for another.
 */
export const PASSWORD_RESET_LINK_MINUTES = 15;
export const EMAIL_VERIFICATION_LINK_MINUTES = 30;
/**
 * The change-of-email link. It is signed with the SAME lifetime option as the verification link
 * (`emailVerification.expiresIn`: the library has no separate one), so this must equal
 * EMAIL_VERIFICATION_LINK_MINUTES; a test pins that. Both exist so the copy names the right one.
 */
export const EMAIL_CHANGE_LINK_MINUTES = 30;

/** The longest display name `update-user` accepts (after trimming). */
export const PROFILE_NAME_MAX_LENGTH = 100;

/**
 * How long a team invitation link works, in hours. Shared by the server (the row's `expires_at`),
 * the email and the Account screen's copy, so the three cannot drift. A proposal awaiting founder
 * confirmation: long enough to survive a weekend, short enough that an old email is not a standing key.
 */
export const TEAM_INVITE_LINK_HOURS = 72;

/** At most this many pending (unaccepted, unrevoked, unexpired) invitations per workspace. */
export const MAX_PENDING_INVITATIONS = 20;

/** The longest email address accepted (the RFC 5321 path limit). */
export const INVITE_EMAIL_MAX_LENGTH = 254;

export const WORKSPACE_ROLE = ["owner", "member", "tenant", "guarantor"] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLE)[number];

/** The longest workspace name POST /workspaces accepts (after trimming). */
export const WORKSPACE_NAME_MAX_LENGTH = 100;

/**
 * How many workspaces one user may own before POST /workspaces refuses another (409
 * `workspace_limit_reached`). Any signed-in user may create a workspace, and each creation writes
 * a tenant root plus a membership, so without a cap one account could mint unbounded tenants. A
 * proposal awaiting founder confirmation. Counts workspaces the user OWNS (role owner), so being
 * invited into others never uses it up.
 */
export const MAX_WORKSPACES_PER_USER = 10;
